import { Profile, ProfileData } from '@/types/profile';

export type BadgeType = 'admin' | 'verified' | 'premium' | 'online';
export type BadgeIcon = 'Shield' | 'Crown' | 'Circle';

export interface ProfileStatusBadge {
  type: BadgeType;
  icon: BadgeIcon;
  color: string; // Tailwind class
  bgColor: string; // Tailwind class
  animation?: string; // Tailwind animation class
  glowEffect?: string; // CSS box-shadow
  priority: number; // Lower number = higher priority
  label: string;
}

export interface ProfileBorderStyle {
  containerClass: string;
  animation?: string;
  priority: number;
  label: string;
}

export interface ProfileStatus {
  badges: ProfileStatusBadge[];
  borderStyle: ProfileBorderStyle;
}

/**
 * ProfileStatusRules - Centraliza toda a lógica de status de perfis
 * 
 * Padronização de cores:
 * - Admin: Roxo/Dourado com glow
 * - Verificado: Azul com glow pulsante
 * - Premium: Dourado com shine
 * - Online: Verde
 */
export class ProfileStatusRules {
  // Cache de curta duração para renderização em batch de múltiplos cards
  private static adminCache = new Map<string, { isAdmin: boolean; timestamp: number }>();
  private static CACHE_TTL = 5000; // 5 segundos - suficiente para renderização em batch

  /**
   * Limpa entradas expiradas do cache
   */
  private static cleanExpiredCache(): void {
    const now = Date.now();
    for (const [key, value] of this.adminCache.entries()) {
      if (now - value.timestamp > this.CACHE_TTL) {
        this.adminCache.delete(key);
      }
    }
  }

  /**
   * Determina se o usuário é admin
   * Prioriza user_role vindo do feed, depois cache local, depois AdminService
   */
  static async isAdmin(profile: Profile | ProfileData): Promise<boolean> {
    // Se profile já tem user_role (veio do get_feed_data), usar direto
    if ('user_role' in profile && profile.user_role) {
      return profile.user_role === 'admin';
    }

    // Limpar cache expirado periodicamente (10% de chance)
    if (Math.random() < 0.1) {
      this.cleanExpiredCache();
    }

    // Verificar cache local
    const cached = this.adminCache.get(profile.id);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.isAdmin;
    }

    // Fallback para AdminService (que tem seu próprio cache de 5 minutos)
    const { adminService } = await import('@/core/services/AdminService');
    const isAdmin = await adminService.isAdmin(profile.id);

    // Armazenar no cache local
    this.adminCache.set(profile.id, {
      isAdmin,
      timestamp: Date.now()
    });

    return isAdmin;
  }

  /**
   * Determina se o usuário é verificado
   */
  static isVerified(profile: Profile | ProfileData): boolean {
    // Apenas 'full' significa totalmente verificado
    return profile.verification_level === 'full';
  }

  /**
   * Determina se o usuário tem plano pago (qualquer tier)
   */
  static isPremium(profile: Profile | ProfileData): boolean {
    return profile.plan_type === 'sponsor_premium' || 
           profile.plan_type === 'sponsor_standard' || 
           profile.plan_type === 'sponsored_premium';
  }

  /**
   * Determina se o usuário tem o plano top-tier (mais caro)
   * Sponsor: apenas Infinity (sponsor_premium)
   * Sponsored: qualquer plano pago (sponsored_premium)
   */
  static isTopTierPlan(profile: Profile | ProfileData): boolean {
    return profile.plan_type === 'sponsor_premium' || 
           profile.plan_type === 'sponsored_premium';
  }

  /**
   * Determina se o usuário está online
   */
  static isOnline(profile: Profile | ProfileData): boolean {
    return profile.is_online === true;
  }

  /**
   * Retorna o badge de Admin
   */
  private static getAdminBadge(): ProfileStatusBadge {
    return {
      type: 'admin',
      icon: 'Shield',
      color: 'text-white',
      bgColor: 'bg-purple-600',
      animation: 'animate-pulse',
      glowEffect: '0 0 20px rgba(168, 85, 247, 0.8)',
      priority: 1,
      label: 'Administrador'
    };
  }

  /**
   * Retorna o badge de Verificado
   */
  private static getVerifiedBadge(): ProfileStatusBadge {
    return {
      type: 'verified',
      icon: 'Shield',
      color: 'text-white',
      bgColor: 'bg-blue-500',
      animation: 'animate-blue-glow',
      glowEffect: '0 0 15px rgba(59, 130, 246, 0.8)',
      priority: 2,
      label: 'Verificado'
    };
  }

  /**
   * Retorna o badge de Premium
   */
  private static getPremiumBadge(): ProfileStatusBadge {
    return {
      type: 'premium',
      icon: 'Crown',
      color: 'text-black',
      bgColor: 'bg-luxury-gold',
      animation: 'animate-gold-shine',
      glowEffect: '0 0 15px rgba(212, 165, 116, 0.5)',
      priority: 3,
      label: 'Premium'
    };
  }

  /**
   * Retorna o badge de Online
   */
  private static getOnlineBadge(): ProfileStatusBadge {
    return {
      type: 'online',
      icon: 'Circle',
      color: 'text-white',
      bgColor: 'bg-green-500',
      animation: undefined,
      glowEffect: undefined,
      priority: 4,
      label: 'Online'
    };
  }

  /**
   * Retorna todos os badges aplicáveis ao perfil, ordenados por prioridade
   */
  static async getProfileBadges(profile: Profile | ProfileData): Promise<ProfileStatusBadge[]> {
    const badges: ProfileStatusBadge[] = [];

    // Admin tem prioridade máxima
    if (await this.isAdmin(profile)) {
      badges.push(this.getAdminBadge());
    }

    // Verificado
    if (this.isVerified(profile)) {
      badges.push(this.getVerifiedBadge());
    }

    // Premium (não mostrar se já é admin)
    if (this.isPremium(profile) && !(await this.isAdmin(profile))) {
      badges.push(this.getPremiumBadge());
    }

    // Online
    if (this.isOnline(profile)) {
      badges.push(this.getOnlineBadge());
    }

    // Ordenar por prioridade
    return badges.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Retorna o estilo de borda do perfil baseado no status
   * Nova hierarquia: Admin > TopTier (dourado brilhante) > Paid (dourado sutil) > Online > Default
   */
  static async getProfileBorderStyle(profile: Profile | ProfileData): Promise<ProfileBorderStyle> {
    const isAdmin = await this.isAdmin(profile);
    const isPremium = this.isPremium(profile);
    const isTopTier = this.isTopTierPlan(profile);
    const isOnline = this.isOnline(profile);

    // Prioridade 1: Admin - Roxo pulsante
    if (isAdmin) {
      return {
        containerClass: 'border-2 border-purple-600 shadow-[0_0_20px_rgba(168,85,247,0.6)]',
        animation: 'animate-pulse',
        priority: 1,
        label: 'Administrador'
      };
    }

    // Prioridade 2: Top Tier (Infinity/Sponsored Premium) - Dourado elegante com glow
    if (isTopTier) {
      return {
        containerClass: 'border-2 border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.4)]',
        animation: 'animate-gold-glow',
        priority: 2,
        label: 'Infinity'
      };
    }

    // Prioridade 3: Plano pago (Inspire) - Dourado sutil sem glow
    if (isPremium) {
      return {
        containerClass: 'border-2 border-amber-400/50',
        priority: 3,
        label: 'Inspire'
      };
    }

    // Prioridade 4: Online - Verde
    if (isOnline) {
      return {
        containerClass: 'border-2 border-green-500',
        priority: 4,
        label: 'Online'
      };
    }

    // Padrão: Sem borda especial
    return {
      containerClass: 'border border-white/10',
      priority: 5,
      label: 'Padrão'
    };
  }

  /**
   * Método principal: retorna status completo do perfil
   * Otimizado para fazer apenas UMA verificação de isAdmin em vez de duas
   */
  static async getProfileStatus(profile: Profile | ProfileData): Promise<ProfileStatus> {
    // Verificar status UMA VEZ para evitar chamadas duplicadas
    const isAdmin = await this.isAdmin(profile);
    const isPremium = this.isPremium(profile);
    const isVerified = this.isVerified(profile);
    const isOnline = this.isOnline(profile);

    return {
      badges: this.getProfileBadgesSync(profile, { isAdmin, isPremium, isVerified, isOnline }),
      borderStyle: this.getProfileBorderStyleSync(profile, { isAdmin, isPremium, isVerified, isOnline })
    };
  }

  /**
   * Versão síncrona de getProfileBadges que recebe status pré-calculados
   */
  private static getProfileBadgesSync(
    profile: Profile | ProfileData,
    status: { isAdmin: boolean; isPremium: boolean; isVerified: boolean; isOnline: boolean }
  ): ProfileStatusBadge[] {
    const badges: ProfileStatusBadge[] = [];

    if (status.isAdmin) {
      badges.push(this.getAdminBadge());
    }

    if (status.isVerified) {
      badges.push(this.getVerifiedBadge());
    }

    if (status.isPremium && !status.isAdmin) {
      badges.push(this.getPremiumBadge());
    }

    if (status.isOnline) {
      badges.push(this.getOnlineBadge());
    }

    return badges.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Versão síncrona de getProfileBorderStyle que recebe status pré-calculados
   * Nova hierarquia: Admin > TopTier (dourado brilhante) > Paid (dourado sutil) > Online > Default
   */
  private static getProfileBorderStyleSync(
    profile: Profile | ProfileData,
    status: { isAdmin: boolean; isPremium: boolean; isVerified: boolean; isOnline: boolean }
  ): ProfileBorderStyle {
    const isTopTier = this.isTopTierPlan(profile);

    // Prioridade 1: Admin - Roxo pulsante
    if (status.isAdmin) {
      return {
        containerClass: 'border-2 border-purple-600 shadow-[0_0_20px_rgba(168,85,247,0.6)]',
        animation: 'animate-pulse',
        priority: 1,
        label: 'Administrador'
      };
    }

    // Prioridade 2: Top Tier (Infinity/Sponsored Premium) - Dourado elegante com glow
    if (isTopTier) {
      return {
        containerClass: 'border-2 border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.4)]',
        animation: 'animate-gold-glow',
        priority: 2,
        label: 'Infinity'
      };
    }

    // Prioridade 3: Plano pago (Inspire) - Dourado sutil sem glow
    if (status.isPremium) {
      return {
        containerClass: 'border-2 border-amber-400/50',
        priority: 3,
        label: 'Inspire'
      };
    }

    // Prioridade 4: Online - Verde
    if (status.isOnline) {
      return {
        containerClass: 'border-2 border-green-500',
        priority: 4,
        label: 'Online'
      };
    }

    // Padrão: Sem borda especial
    return {
      containerClass: 'border border-white/10',
      priority: 5,
      label: 'Padrão'
    };
  }
}
